import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { Add, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import ClearIcon from 'assets/doc_icons/close_icon.svg';
import CustomInputField from 'ui-components/CustomInputField';
import { Secondary } from 'ui-components/Buttons';
import Section from './Section';

const slugify = (text, taken) => {
    const base = String(text || 'faq')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40) || 'faq';
    let candidate = base;
    let suffix = 2;
    while (taken.includes(candidate)) candidate = `${base}_${suffix++}`;
    return candidate;
};

const move = (list, from, to) => {
    if (to < 0 || to >= list.length) return list;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/**
 * The rest of the pricing page: its headline, its intro and its questions.
 *
 * These lived in the frontend while the cards and the table became editable,
 * which left about a third of what a visitor reads needing a developer. The
 * page falls back to the old copy when this section is missing, so an
 * unseeded environment still renders.
 */
const PageContentEditor = ({ page, onChange }) => {
    const theme = useTheme();

    if (!page) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.customColors.whiteBG,
                border: `1px solid ${theme.palette.customColors.b50}`,
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
            }}>
                <Typography sx={{ fontWeight: 700, color: theme.palette.customColors.g300 }}>
                    No page content yet
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, marginTop: 0.5 }}>
                    Run <strong>npm run plans:page</strong> once to bring across the headline and questions the
                    pricing page uses today. After that they are edited here.
                </Typography>
            </Box>
        );
    }

    const hero = page.hero || {};
    const faqs = page.faqs || [];
    const update = (changes) => onChange({ ...page, ...changes });
    const updateHero = (changes) => update({ hero: { ...hero, ...changes } });
    const updateFaq = (index, changes) => {
        const next = [...faqs];
        next[index] = { ...next[index], ...changes };
        update({ faqs: next });
    };

    return (
        <Box display="grid" gap="1rem">
            <Section title="Header" description="The first thing anyone reads on the pricing page.">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <CustomInputField label="Label above headline" value={hero.eyebrow || ''}
                            onChange={(event) => updateHero({ eyebrow: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <CustomInputField label="Headline" value={hero.title || ''}
                            onChange={(event) => updateHero({ title: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <CustomInputField label="Intro" multiline minRows={2} value={hero.subtitle || ''}
                            onChange={(event) => updateHero({ subtitle: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <CustomInputField label="Button text" value={hero.button_text || ''}
                            onChange={(event) => updateHero({ button_text: event.target.value })} />
                    </Grid>
                </Grid>
            </Section>

            <Section title={`Questions (${faqs.length})`} description="Shown at the bottom of the pricing page, in this order.">
                <Box display="grid" gap="0.75rem">
                    {faqs.map((faq, index) => (
                        <Box key={faq.id || index} sx={{
                            position: 'relative',
                            border: `1px solid ${theme.palette.customColors.b50}`,
                            borderRadius: '8px',
                            padding: '0.75rem',
                        }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CustomInputField
                                    value={faq.question || ''}
                                    placeholder="Question"
                                    inputProps={{ 'aria-label': `Question ${index + 1}` }}
                                    onChange={(event) => updateFaq(index, { question: event.target.value })}
                                    sx={{ marginBottom: 0, '& .MuiFilledInput-root': { fontWeight: 600 } }}
                                />
                                <Tooltip title="Move up">
                                    <span>
                                        <Box component="button" type="button" disabled={index === 0}
                                            onClick={() => update({ faqs: move(faqs, index, index - 1) })}
                                            sx={{ border: 'none', background: 'none', cursor: 'pointer', color: theme.palette.customColors.g75, display: 'flex' }}>
                                            <ArrowUpward fontSize="small" />
                                        </Box>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Move down">
                                    <span>
                                        <Box component="button" type="button" disabled={index === faqs.length - 1}
                                            onClick={() => update({ faqs: move(faqs, index, index + 1) })}
                                            sx={{ border: 'none', background: 'none', cursor: 'pointer', color: theme.palette.customColors.g75, display: 'flex' }}>
                                            <ArrowDownward fontSize="small" />
                                        </Box>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Remove question">
                                    <img
                                        src={ClearIcon}
                                        alt="Remove question"
                                        style={{ height: '1rem', width: '1rem', cursor: 'pointer', flexShrink: 0 }}
                                        onClick={() => update({ faqs: faqs.filter((_, i) => i !== index) })}
                                    />
                                </Tooltip>
                            </Box>
                            <Box marginTop="0.5rem">
                                <CustomInputField
                                    value={faq.answer || ''}
                                    multiline
                                    minRows={2}
                                    placeholder="Answer"
                                    inputProps={{ 'aria-label': `Answer ${index + 1}` }}
                                    onChange={(event) => updateFaq(index, { answer: event.target.value })}
                                    sx={{ marginBottom: 0 }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Secondary startIcon={<Add />} sx={{ marginTop: '0.75rem' }}
                    onClick={() => update({
                        faqs: [...faqs, {
                            id: slugify(`question_${faqs.length + 1}`, faqs.map((faq) => faq.id)),
                            question: '',
                            answer: '',
                        }],
                    })}>
                    Add question
                </Secondary>
            </Section>
        </Box>
    );
};

export default PageContentEditor;
